/** Record-to-picture: reel + mic start together; voice t=0 = reel t=0. */

export const OFFSET_ALIGN_THRESHOLD_MS = 20;

export type VideoPlaybackSnapshot = {
  muted: boolean;
  volume: number;
  controls: boolean;
};

export type SyncedCaptureStart = {
  offsetMs: number;
  playStartedAt: number;
  recordStartedAt: number;
};

type StartableRecorder = {
  start: () => void;
  addEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) => void;
};

export function formatMss(sec: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(sec) ? sec : 0));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatRecordTimer(elapsedSec: number, totalSec: number): string {
  return `${formatMss(elapsedSec)} / ${formatMss(totalSec)}`;
}

export function recordProgressPercent(elapsedSec: number, totalSec: number): number {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return 0;
  return Math.max(0, Math.min(100, (elapsedSec / totalSec) * 100));
}

/** Positive = recorder late (pad). Negative = recorder early (trim). */
export function computeVoiceAlignOffsetMs(
  recordStartedAt: number,
  playStartedAt: number
): number {
  if (!Number.isFinite(recordStartedAt) || !Number.isFinite(playStartedAt)) {
    return 0;
  }
  return recordStartedAt - playStartedAt;
}

export function shouldAlignOffset(offsetMs: number): boolean {
  return Math.abs(offsetMs) >= OFFSET_ALIGN_THRESHOLD_MS;
}

export function alignPcm(
  samples: Float32Array,
  sampleRate: number,
  offsetMs: number
): Float32Array {
  if (!shouldAlignOffset(offsetMs) || sampleRate <= 0) return samples;
  const offsetSamples = Math.round((offsetMs / 1000) * sampleRate);
  if (offsetSamples > 0) {
    const out = new Float32Array(samples.length + offsetSamples);
    out.set(samples, offsetSamples);
    return out;
  }
  const trim = Math.min(samples.length, -offsetSamples);
  return samples.subarray(trim);
}

export function encodeWav(channelData: Float32Array, sampleRate: number): Blob {
  const rate = Math.max(1, Math.round(sampleRate));
  const pcm = new Int16Array(channelData.length);
  for (let i = 0; i < channelData.length; i += 1) {
    const s = Math.max(-1, Math.min(1, channelData[i]));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new ArrayBuffer(44 + pcm.byteLength);
  const view = new DataView(bytes);
  const writeStr = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + pcm.byteLength, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, pcm.byteLength, true);
  new Uint8Array(bytes, 44).set(new Uint8Array(pcm.buffer));
  return new Blob([bytes], { type: 'audio/wav' });
}

export async function alignRecordedVoiceBlob(
  blob: Blob,
  offsetMs: number,
  decode: (data: ArrayBuffer) => Promise<AudioBuffer> = decodeAudioBuffer
): Promise<{ blob: Blob; offsetMs: number; aligned: boolean }> {
  if (!shouldAlignOffset(offsetMs)) {
    return { blob, offsetMs, aligned: false };
  }
  try {
    const buffer = await Promise.race([
      decode(await blob.arrayBuffer()),
      new Promise<AudioBuffer>((_, reject) => {
        window.setTimeout(() => reject(new Error('decode timeout')), 200);
      }),
    ]);
    const channel = buffer.numberOfChannels > 0 ? buffer.getChannelData(0) : new Float32Array(0);
    const aligned = alignPcm(channelDataCopy(channel), buffer.sampleRate, offsetMs);
    return {
      blob: encodeWav(aligned, buffer.sampleRate),
      offsetMs,
      aligned: true,
    };
  } catch {
    return { blob, offsetMs, aligned: false };
  }
}

function channelDataCopy(channel: Float32Array): Float32Array {
  const copy = new Float32Array(channel.length);
  copy.set(channel);
  return copy;
}

async function decodeAudioBuffer(data: ArrayBuffer): Promise<AudioBuffer> {
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) throw new Error('no AudioContext');
  const ctx = new Ctor();
  try {
    return await ctx.decodeAudioData(data.slice(0));
  } finally {
    void ctx.close();
  }
}

export function snapshotVideoPlayback(video: HTMLVideoElement): VideoPlaybackSnapshot {
  return {
    muted: video.muted,
    volume: video.volume,
    controls: video.controls,
  };
}

export function restoreVideoPlayback(
  video: HTMLVideoElement,
  snap: VideoPlaybackSnapshot | null
): void {
  if (!snap) return;
  video.muted = snap.muted;
  video.volume = snap.volume;
  video.controls = snap.controls;
  try {
    video.pause();
  } catch {
    // jsdom
  }
}

/**
 * Seek reel to 0, optionally mute, then start video.play() and recorder.start()
 * in the same tick. Resolves with the measured play↔record delta.
 */
export function startSyncedCapture(opts: {
  video: HTMLVideoElement | null;
  recorder: StartableRecorder;
  muteReel: boolean;
  now?: () => number;
}): Promise<SyncedCaptureStart> {
  const now = opts.now || (() => performance.now());
  const t0 = now();
  return new Promise((resolve) => {
    let playStartedAt = t0;
    let recordStartedAt = t0;
    let playSeen = !opts.video;
    let recordSeen = false;
    let settled = false;
    const finish = () => {
      if (settled || !playSeen || !recordSeen) return;
      settled = true;
      resolve({
        offsetMs: computeVoiceAlignOffsetMs(recordStartedAt, playStartedAt),
        playStartedAt,
        recordStartedAt,
      });
    };
    if (opts.video) {
      opts.video.controls = false;
      opts.video.muted = opts.muteReel;
      try {
        opts.video.currentTime = 0;
      } catch {
        // jsdom
      }
      opts.video.addEventListener(
        'playing',
        () => {
          playStartedAt = now();
          playSeen = true;
          finish();
        },
        { once: true }
      );
    }
    opts.recorder.addEventListener(
      'start',
      () => {
        recordStartedAt = now();
        recordSeen = true;
        finish();
      },
      { once: true }
    );
    if (opts.video) {
      void opts.video.play().catch(() => {
        playStartedAt = now();
        playSeen = true;
        finish();
      });
    }
    opts.recorder.start();
    window.setTimeout(() => {
      if (!playSeen) {
        playStartedAt = t0;
        playSeen = true;
      }
      if (!recordSeen) {
        recordStartedAt = t0;
        recordSeen = true;
      }
      finish();
    }, 80);
  });
}

export function probeBlobDurationSec(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (value: number) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    let url = '';
    try {
      url = URL.createObjectURL(blob);
    } catch {
      done(0);
      return;
    }
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const duration = Number(audio.duration);
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      done(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };
    audio.onerror = () => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      done(0);
    };
    try {
      audio.src = url;
    } catch {
      done(0);
      return;
    }
    window.setTimeout(() => done(0), 250);
  });
}
